import { useRef, useState } from "preact/hooks";
import { uploadDocument, uploadPostprocessor } from "../api/Api";
import { LoaderCircle, Pencil, Upload } from "lucide-preact";
import style from "./FileUploader.module.css";

export type UploaderType = 'document' | 'postprocessor'

interface FileUploaderProps {
  type: UploaderType;
  onUpload: () => void;
}

export function FileUploader({ type, onUpload }: FileUploaderProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  function handleFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      setSelectedFile(input.files[0]);
    }
  }

  async function handleUploadButtonClick(event: Event) {
    event.preventDefault();
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      if (type === 'document') {
        await uploadDocument(selectedFile);
      } else {
        await uploadPostprocessor(selectedFile);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert(`Upload failed: ${error}`);
    }
    setIsUploading(false);
    onUpload();
  }

  function handleEditNameButtonClick(event: Event) {
    event.preventDefault();
    if (!selectedFile) return;

    const newName = prompt("Enter new file name:", selectedFile.name);
    if (newName) {
      const renamedFile = new File([selectedFile], newName, { type: selectedFile.type });
      setSelectedFile(renamedFile);
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(renamedFile);
        fileInputRef.current.files = dataTransfer.files;
      }
    }
  }

  return (
    <form class={style.fileUploader} onSubmit={(e) => { void handleUploadButtonClick(e) }}>
      <div class={"input " + style.fileInput}>
        <input
          type="file"
          accept={type === 'document' ? 'text/plain' : 'application/wasm'}
          onChange={(e) => { handleFileChange(e) }}
          ref={fileInputRef}
        />
      </div>
      <button title="Edit Name" class={style.editNameButton} disabled={!selectedFile || isUploading} onClick={(e) => { handleEditNameButtonClick(e); }}>
        <Pencil />
      </button>
      <button class={style.uploadButton} type="submit" disabled={!selectedFile || isUploading}>
        {isUploading ? <span class="loader"><LoaderCircle /></span> : <Upload />}
        Upload
      </button>
    </form>
  );
}